import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faBuilding, 
  faDharmachakra, 
  faHandshake,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { getDashboardStats, getAllWheels } from '../../services/adminApi.ts';
import { Wheel } from '../../services/adminApi.ts';

const DashboardContainer = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  color: #333;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatTitle = styled.h3`
  font-size: 1rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ChartTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.2rem;
  color: #2c3e50;
`;

const TableSection = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TableTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.2rem;
  color: #2c3e50;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f5f7fa;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableHeader = styled.th`
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #7f8c8d;
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const WheelsContainer = styled.div`
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const WheelsTitle = styled.h2`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const WheelsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const WheelCard = styled.div`
  background-color: #f7fafc;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const WheelName = styled.h3`
  font-size: 1rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
`;

const WheelDetail = styled.p`
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.25rem;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  background-color: #4299e1;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  text-decoration: none;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: #3182ce;
  }
`;

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Get user type from localStorage
    const storedUserType = localStorage.getItem('user_type');
    setUserType(storedUserType);

    // Get user data from localStorage
    const userDataString = localStorage.getItem('admin_user');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);
    }

    const fetchData = async () => {
      try {
        if (storedUserType === 'Admin') {
          // Fetch admin dashboard stats
          const data = await getDashboardStats();
          setStats(data);
        } else if (storedUserType === 'BusinessUser') {
          // Fetch business user's wheels
          const wheelsData = await getAllWheels();
          
          // Get business user ID from localStorage
          const businessUserId = userData?.id;
          
          // Filter wheels for this business user
          if (businessUserId) {
            const userWheels = wheelsData.filter(wheel => wheel.businessUserId === businessUserId);
            setWheels(userWheels);
          } else {
            setWheels([]);
          }
        }
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
        if (err.response?.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          <LoadingIcon icon={faSpinner} />
          Loading dashboard...
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorContainer>{error}</ErrorContainer>
      </DashboardContainer>
    );
  }

  // Render admin dashboard
  if (userType === 'Admin') {
    return (
      <DashboardContainer>
        <Title>Admin Dashboard</Title>
        <StatsGrid>
          <StatCard>
            <StatTitle>Total Users</StatTitle>
            <StatValue>{stats?.totalUsers || 0}</StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Active Wheels</StatTitle>
            <StatValue>{stats?.activeWheels || 0}</StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Business Users</StatTitle>
            <StatValue>{stats?.businessUsers || 0}</StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Total Revenue</StatTitle>
            <StatValue>${stats?.totalRevenue?.toFixed(2) || '0.00'}</StatValue>
          </StatCard>
        </StatsGrid>
      </DashboardContainer>
    );
  }
  
  // Render business user dashboard
  return (
    <DashboardContainer>
      <Title>Business Dashboard</Title>
      <StatsGrid>
        <StatCard>
          <StatTitle>Total Wheels</StatTitle>
          <StatValue>{wheels.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Active Wheels</StatTitle>
          <StatValue>{wheels.filter(wheel => wheel.status === 'Active').length}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Participants</StatTitle>
          <StatValue>
            {wheels.reduce((total, wheel) => total + wheel.slots.filter(slot => slot.userId).length, 0)}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Matches</StatTitle>
          <StatValue>{wheels.reduce((total, wheel) => total + wheel.matches.length, 0)}</StatValue>
        </StatCard>
      </StatsGrid>

      <WheelsContainer>
        <WheelsTitle>Your Wheels</WheelsTitle>
        {wheels.length === 0 ? (
          <p>No wheels found. Create your first wheel to get started!</p>
        ) : (
          <WheelsList>
            {wheels.map(wheel => (
              <WheelCard key={wheel.id}>
                <WheelName>{wheel.name}</WheelName>
                <WheelDetail>Status: {wheel.status}</WheelDetail>
                <WheelDetail>Slots: {wheel.slotCount}</WheelDetail>
                <WheelDetail>Created: {new Date(wheel.createdAt).toLocaleDateString()}</WheelDetail>
                <ActionButton to={`/admin/wheels/${wheel.id}`}>View Details</ActionButton>
              </WheelCard>
            ))}
          </WheelsList>
        )}
      </WheelsContainer>
    </DashboardContainer>
  );
};

export default AdminDashboard; 